/**
 * Chunk Upload Utility
 * Handles chunked video uploads with resume capability
 */

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB per chunk
const UPLOAD_STATE_KEY = 'foh_upload_state';

/**
 * Get saved upload state from localStorage
 * @returns {object|null} Upload state or null if none exists
 */
export const getUploadState = () => {
  try {
    const stateJson = localStorage.getItem(UPLOAD_STATE_KEY);
    if (!stateJson) return null;
    
    const state = JSON.parse(stateJson);
    
    // Check if state is expired (older than 24 hours)
    const ageInHours = (Date.now() - state.timestamp) / (1000 * 60 * 60);
    if (ageInHours > 24) {
      clearUploadState();
      return null;
    }
    
    return state;
  } catch (error) {
    console.error('Failed to get upload state:', error);
    return null;
  }
};

/**
 * Save upload state to localStorage
 * @param {object} state - Upload state to save
 */
export const saveUploadState = (state) => {
  try {
    localStorage.setItem(UPLOAD_STATE_KEY, JSON.stringify({
      ...state,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.error('Failed to save upload state:', error);
  }
};

/**
 * Clear upload state from localStorage
 */
export const clearUploadState = () => {
  try {
    localStorage.removeItem(UPLOAD_STATE_KEY);
  } catch (error) {
    console.error('Failed to clear upload state:', error);
  }
};

/**
 * Split blob into chunks
 * @param {Blob} blob - Video blob to split
 * @param {number} chunkSize - Size of each chunk in bytes
 * @returns {Array<{blob: Blob, index: number, start: number, end: number}>}
 */
export const createChunks = (blob, chunkSize = CHUNK_SIZE) => {
  const chunks = [];
  const totalChunks = Math.ceil(blob.size / chunkSize);
  
  for (let i = 0; i < totalChunks; i++) {
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, blob.size);
    const chunkBlob = blob.slice(start, end);
    
    chunks.push({
      blob: chunkBlob,
      index: i,
      start,
      end,
      size: chunkBlob.size
    });
  }
  
  return chunks;
};

/**
 * Upload a single chunk with retry logic
 * @param {string} uploadUrl - Backend URL for chunk upload
 * @param {Blob} chunkBlob - Chunk data to upload
 * @param {number} chunkIndex - Index of the chunk
 * @param {number} totalChunks - Total number of chunks
 * @param {string} uploadId - Unique upload identifier
 * @param {number} retries - Number of retries (default: 3)
 * @returns {Promise<object>} Response from server
 */
export const uploadChunk = async (uploadUrl, chunkBlob, chunkIndex, totalChunks, uploadId, retries = 3, authToken = null) => {
  let lastError;
  
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      if (attempt > 0) {
        console.log(`🔄 Retrying chunk ${chunkIndex} (attempt ${attempt + 1}/${retries + 1})...`);
        // Wait a bit before retrying
        await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
      }
      
      const formData = new FormData();
      formData.append('chunk', chunkBlob);
      formData.append('chunkIndex', chunkIndex.toString());
      formData.append('totalChunks', totalChunks.toString());
      formData.append('uploadId', uploadId);
      
      // Log FormData contents for debugging (especially for chunk 0)
      if (chunkIndex === 0) {
        console.log('📋 Chunk 0 FormData:', {
          chunkSize: chunkBlob.size,
          chunkIndex,
          totalChunks,
          uploadId
        });
      }
      
      const headers = {};
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers,
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Upload failed' }));
        throw new Error(error.message || `Chunk ${chunkIndex} upload failed`);
      }
      
      return response.json();
    } catch (error) {
      lastError = error;
      console.error(`❌ Chunk ${chunkIndex} upload failed (attempt ${attempt + 1}/${retries + 1}):`, error.message);
      
      // If this is the last retry, throw the error
      if (attempt === retries) {
        throw lastError;
      }
    }
  }
  
  throw lastError;
};

/**
 * Upload video in chunks with resume capability
 * @param {Blob} videoBlob - Video blob to upload
 * @param {string} testimonyId - Testimony ID
 * @param {string} fileName - File name
 * @param {function} onProgress - Progress callback (percentage, currentChunk, totalChunks)
 * @param {object} existingState - Existing upload state to resume from
 * @returns {Promise<{publicUrl: string, uploadId: string}>}
 */
/**
 * Upload video in chunks with resume capability
 * @param {Blob} videoBlob - Video blob to upload
 * @param {string} testimonyId - Testimony ID
 * @param {string} fileName - File name
 * @param {function} onProgress - Progress callback (percentage, currentChunk, totalChunks)
 * @param {object} existingState - Existing upload state to resume from
 * @returns {Promise<{publicUrl: string, uploadId: string}>}
 */
export const uploadInChunks = async (
  videoBlob,
  testimonyId,
  fileName,
  onProgress = null,
  existingState = null,
  authToken = null
) => {
  try {
    // Generate or reuse upload ID
    const uploadId = existingState?.uploadId || `${testimonyId}_${Date.now()}`;
    
    // Create chunks
    const chunks = createChunks(videoBlob);
    const totalChunks = chunks.length;
    
    // Determine starting point
    const startIndex = existingState?.lastCompletedChunk !== undefined 
      ? existingState.lastCompletedChunk + 1 
      : 0;
    
    console.log(`📦 Total chunks: ${totalChunks}, Starting from chunk: ${startIndex}`);
    
    // Upload URL endpoint
    const uploadUrl = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001/api'}/testimony/${testimonyId}/upload-chunk`;
    
    // Save initial state
    saveUploadState({
      uploadId,
      testimonyId,
      fileName,
      totalChunks,
      lastCompletedChunk: startIndex - 1,
      videoSize: videoBlob.size
    });
    
    // Upload chunks sequentially
    for (let i = startIndex; i < totalChunks; i++) {
      const chunk = chunks[i];
      
      console.log(`📤 Uploading chunk ${i + 1}/${totalChunks} (index: ${i}, size: ${chunk.size} bytes, uploadId: ${uploadId})`);
      
      try {
        const result = await uploadChunk(uploadUrl, chunk.blob, i, totalChunks, uploadId, 3, authToken);
        console.log(`✅ Chunk ${i} uploaded successfully:`, result);
      } catch (chunkError) {
        // Log error but don't clear state - allow resume
        console.error(`Failed to upload chunk ${i + 1}:`, chunkError.message);
        throw new Error(`Upload interrupted at chunk ${i + 1} of ${totalChunks}. You can resume later.`);
      }
      
      // Update state after successful chunk upload
      saveUploadState({
        uploadId,
        testimonyId,
        fileName,
        totalChunks,
        lastCompletedChunk: i,
        videoSize: videoBlob.size
      });
      
      // Update progress
      if (onProgress) {
        const percentage = ((i + 1) / totalChunks) * 100;
        onProgress(percentage, i + 1, totalChunks);
      }
    }
    
    // All chunks uploaded, finalize
    console.log('✅ All chunks uploaded, finalizing...');
    console.log(`📊 Upload summary: uploadId=${uploadId}, totalChunks=${totalChunks}, videoSize=${videoBlob.size}`);
    
    // Verify upload state before finalization
    const finalState = getUploadState();
    console.log('📋 Final state before finalization:', finalState);
    
    // Ensure all chunks were marked as complete
    if (finalState && finalState.lastCompletedChunk !== totalChunks - 1) {
      console.error('⚠️ Upload state mismatch!', {
        expected: totalChunks - 1,
        actual: finalState.lastCompletedChunk
      });
    }
    
    // Give backend a moment to process all chunks
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const finalizeUrl = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001/api'}/testimony/${testimonyId}/finalize-upload`;
    
    let finalizeResponse;
    try {
      const finalizeHeaders = { 'Content-Type': 'application/json' };
      if (authToken) finalizeHeaders['Authorization'] = `Bearer ${authToken}`;

      finalizeResponse = await fetch(finalizeUrl, {
        method: 'POST',
        headers: finalizeHeaders,
        body: JSON.stringify({
          uploadId,
          fileName,
          totalChunks
        })
      });
    } catch (networkError) {
      console.error('Network error during finalization:', networkError);
      throw new Error('Network error occurred. Upload saved and can be resumed.');
    }
    
    if (!finalizeResponse.ok) {
      let errorMessage = 'Failed to complete upload';
      let errorDetails = null;
      try {
        const error = await finalizeResponse.json();
        errorMessage = error.message || errorMessage;
        errorDetails = error;
        console.error('Finalization failed:', {
          uploadId,
          totalChunks,
          error: errorDetails
        });
      } catch (e) {
        // Ignore JSON parse error
        console.error('Finalization failed with status:', finalizeResponse.status);
      }
      throw new Error(`${errorMessage} (uploadId: ${uploadId}, chunks: ${totalChunks})`);
    }
    
    const result = await finalizeResponse.json();
    
    // Clear upload state on success
    clearUploadState();
    
    return {
      publicUrl: result.data.url || result.data.publicUrl, // Backend returns 'url', fallback to 'publicUrl'
      uploadId
    };
  } catch (error) {
    console.error('Chunk upload error:', error);
    // Don't clear upload state - allow resume attempt
    throw error;
  }
};

/**
 * Check if there's an incomplete upload that can be resumed
 * @param {string} testimonyId - Testimony ID to check
 * @returns {object|null} Upload state if resumable, null otherwise
 */
export const getResumableUpload = (testimonyId) => {
  const state = getUploadState();
  
  if (!state) return null;
  
  // Check if it's for the same testimony
  if (state.testimonyId !== testimonyId) return null;
  
  // Check if there are chunks left to upload
  if (state.lastCompletedChunk >= state.totalChunks - 1) {
    // Upload was actually complete, just clean up
    clearUploadState();
    return null;
  }
  
  return state;
};
